import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useAnalyticalVariableLookupQuery } from '../api/useAnalyticalVariableLookupQuery';
import { useVariablePreviewRenderer } from '../context/VariablePreviewContext';
import { useAnalysisDefinitionStore } from '../state/useAnalysisDefinitionStore';
import type {
  SelectedDatasetSource,
  UseVariableSourceListResult,
  VariableSourceRowModel,
} from '../types';
import { useAnalysisEditLock } from './useAnalysisEditLock';
import { useAnalysisRouteParams } from './useAnalysisRouteParams';
import { useAvPreview } from './useAvPreview';
import { DEFAULT_DATASET_VARIABLES } from '../utils/analysisSources';

const NUMBER_PAD_LENGTH = 2;

const toTypeLabel = (format: string | null | undefined): string | null =>
  format ? format.toUpperCase() : null;

const padNumber = (position: number): string => String(position).padStart(NUMBER_PAD_LENGTH, '0');

type FormatMessage = ReturnType<typeof useIntl>['formatMessage'];

/** The row's table-usage text -- shown for every row except a `default` one,
 * which shows its own "Default" badge instead. */
const getUsageLabel = (formatMessage: FormatMessage, usedInTableNumbers: number[]): string =>
  usedInTableNumbers.length > 0
    ? usedInTableNumbers
        .map((number) =>
          formatMessage({ id: 'analysisDetail.tables.preview.variableCard.chip.table' }, { number })
        )
        .join(', ')
    : formatMessage({ id: 'analysisDetail.tables.preview.variableCard.chip.notUsed' });

export const useVariableSourceList = (
  datasetSources: SelectedDatasetSource[]
): UseVariableSourceListResult => {
  const { formatMessage } = useIntl();
  const { projectId } = useAnalysisRouteParams();
  const renderVariablePreview = useVariablePreviewRenderer();
  const { previewAvId, onOpenPreview, onClosePreview } = useAvPreview();
  const { data: lookupResponse, isLoading } = useAnalyticalVariableLookupQuery({ projectId });
  const removeSource = useAnalysisDefinitionStore((state) => state.removeSource);
  const reorderSources = useAnalysisDefinitionStore((state) => state.reorderSources);
  const { isEditDisabled, isProcessing } = useAnalysisEditLock();

  const itemsById = useMemo(
    () => new Map((lookupResponse?.data ?? []).map((item) => [item.analyticalVariableId, item])),
    [lookupResponse]
  );

  const rows = useMemo<VariableSourceRowModel[]>(() => {
    const defaultRows = DEFAULT_DATASET_VARIABLES.map<VariableSourceRowModel>((spec, index) => ({
      key: spec.key,
      avId: null,
      number: padNumber(index + 1),
      typeLabel: spec.typeLabel,
      title: formatMessage({ id: spec.nameMessageId }),
      status: 'default',
      isUsedInTable: false,
      chipLabel: getUsageLabel(formatMessage, []),
      onClick: null,
      // A default entry has no backing source to remove.
      onDelete: null,
    }));

    const sourceRows = datasetSources.map<VariableSourceRowModel>((source, index) => {
      const number = padNumber(index + DEFAULT_DATASET_VARIABLES.length + 1);
      if (source.kind === 'intent') {
        const isUsedInTable = source.usedInTableNumbers.length > 0;
        return {
          key: source.key,
          avId: null,
          number,
          typeLabel: toTypeLabel(source.expectedOutputFormat),
          title: source.label,
          status: 'intent',
          isUsedInTable,
          chipLabel: getUsageLabel(formatMessage, source.usedInTableNumbers),
          onClick: null,
          onDelete: isUsedInTable ? null : () => removeSource(source.key),
        };
      }
      const item = itemsById.get(source.avId);
      const isUsedInTable = source.usedInTableNumbers.length > 0;
      return {
        key: source.key,
        avId: source.avId,
        number,
        // The library's format wins; the definition's expected format covers the
        // gap before the lookup resolves or when the variable is not listed.
        typeLabel: toTypeLabel(item?.format ?? source.expectedOutputFormat),
        // Same label field the analytical-dataset variable rows use.
        title: item?.dataElement ?? item?.name ?? source.avId,
        // TODO: once the backend can signal that a used variable's avId
        // hasn't resolved to a library item, surface a "Needs your input"
        // hint for that row instead of treating it as an ordinary used row.
        status: isUsedInTable ? 'used' : 'unused',
        isUsedInTable,
        chipLabel: getUsageLabel(formatMessage, source.usedInTableNumbers),
        // Plain (non-clickable) row when no preview renderer is injected.
        onClick: renderVariablePreview ? () => onOpenPreview(source.avId) : null,
        // A source can be deleted only while nothing in the definition
        // depends on it -- a row used by a table's `uses` is not.
        onDelete: isUsedInTable ? null : () => removeSource(source.key),
      };
    });

    return [...defaultRows, ...sourceRows];
  }, [
    datasetSources,
    itemsById,
    renderVariablePreview,
    onOpenPreview,
    formatMessage,
    removeSource,
  ]);

  const deleteTooltip = isEditDisabled
    ? formatMessage({
        id: isProcessing
          ? 'analysisDetail.editLock.processing'
          : 'analysisDetail.editLock.notOwner',
      })
    : undefined;

  return {
    rows,
    isLoading,
    previewAvId,
    onClosePreview,
    renderVariablePreview,
    isEditDisabled,
    deleteTooltip,
    onReorder: reorderSources,
  };
};
